/**
 * Template de prise de besoin : Politique d'escalade des incidents v1
 * Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0
 * Définit les responsables, délais d'escalade, critères et communication
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePolitiqueEscaladeIncidentsV1: IntakeTemplate = {
  id: 'politique-escalade-incidents-v1',
  name: 'Prise de besoin Politique d\'escalade des incidents v1',
  description:
    'Questionnaire complet pour élaborer une politique d\'escalade des incidents conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit les responsables, délais d\'escalade, critères et processus de communication.',
  documentTypeCode: 'INCIDENT_ESCALATION_POLICY',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Structure d\'Escalade',
      description: 'Informations sur l\'entreprise et structure d\'escalade',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la politique d\'escalade des incidents.',
        },
        {
          id: 'niveaux_escalade',
          label: 'Combien de niveaux d\'escalade souhaitez-vous ?',
          type: 'select',
          options: ['2 niveaux', '3 niveaux', '4 niveaux', '5 niveaux ou plus', 'À déterminer'],
          helpText: 'ITIL 4: Structure d\'escalade adaptée à la taille et complexité de l\'organisation.',
        },
        {
          id: 'structure_escalade',
          label: 'Quelle structure d\'escalade souhaitez-vous ?',
          type: 'select',
          options: [
            'Escalade hiérarchique (technicien → gestionnaire → direction)',
            'Escalade fonctionnelle (par expertise)',
            'Escalade hybride (hiérarchique + fonctionnelle)',
            'Escalade personnalisée',
            'À déterminer',
          ],
          helpText: 'ITIL 4: Structure d\'escalade pour assurer une résolution efficace des incidents.',
        },
        {
          id: 'ton_document_politique',
          label: 'Quel ton souhaitez-vous pour cette politique ?',
          type: 'select',
          options: [
            'Formel et juridique (ton impératif, sanctions claires)',
            'Pédagogique et collaboratif (ton explicatif, guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la réception et l\'adhésion des parties prenantes.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Niveaux d\'Escalade & Responsables',
      description: 'Définition des niveaux d\'escalade et responsables',
      icon: 'Users',
      questions: [
        {
          id: 'niveau1_responsable',
          label: 'Qui est responsable au niveau 1 (première ligne) ?',
          type: 'text',
          placeholder: 'Ex: Techniciens de première ligne, Support niveau 1...',
          helpText: 'ITIL 4: Responsable du premier niveau d\'escalade.',
        },
        {
          id: 'niveau1_delai',
          label: 'Quel est le délai avant escalade du niveau 1 au niveau 2 ?',
          type: 'text',
          placeholder: 'Ex: 30 minutes pour P1, 2 heures pour P2...',
          helpText: 'ITIL 4: Délais d\'escalade essentiels pour assurer une résolution rapide.',
        },
        {
          id: 'niveau2_responsable',
          label: 'Qui est responsable au niveau 2 (deuxième ligne) ?',
          type: 'text',
          placeholder: 'Ex: Techniciens spécialisés, Gestionnaire d\'incidents...',
          helpText: 'ITIL 4: Responsable du deuxième niveau d\'escalade.',
        },
        {
          id: 'niveau2_delai',
          label: 'Quel est le délai avant escalade du niveau 2 au niveau 3 ?',
          type: 'text',
          placeholder: 'Ex: 1 heure pour P1, 4 heures pour P2...',
          helpText: 'ITIL 4: Délais d\'escalade pour le niveau 2.',
        },
        {
          id: 'niveau3_responsable',
          label: 'Qui est responsable au niveau 3 (troisième ligne / experts) ?',
          type: 'text',
          placeholder: 'Ex: Experts techniques, Fournisseurs, Gestionnaire de service...',
          helpText: 'ITIL 4: Responsable du troisième niveau d\'escalade.',
        },
        {
          id: 'niveau3_delai',
          label: 'Quel est le délai avant escalade du niveau 3 au niveau 4 (direction) ?',
          type: 'text',
          placeholder: 'Ex: 2 heures pour P1, 1 jour pour P2...',
          helpText: 'ITIL 4: Délais d\'escalade pour le niveau 3.',
        },
        {
          id: 'niveau4_responsable',
          label: 'Qui est responsable au niveau 4 (direction) ?',
          type: 'text',
          placeholder: 'Ex: Directeur TI, Directeur général, Comité de direction...',
          helpText: 'ITIL 4: Responsable du niveau d\'escalade ultime.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Critères & Triggers d\'Escalade',
      description: 'Critères et déclencheurs d\'escalade',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'criteres_escalade',
          label: 'Quels sont les critères d\'escalade ?',
          type: 'multiselect',
          options: [
            'Délai de résolution dépassé',
            'Impact business critique',
            'Nombre d\'utilisateurs affectés',
            'Criticité du service',
            'Demande explicite du client',
            'Incident récurrent',
            'Compétence insuffisante',
            'Ressources insuffisantes',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Critères clairs pour déclencher l\'escalade de manière cohérente.',
        },
        {
          id: 'escalade_automatique',
          label: 'Souhaitez-vous mettre en place une escalade automatique ?',
          type: 'select',
          options: ['Oui, pour incidents critiques', 'Oui, selon délais', 'Non', 'À déterminer'],
          helpText: 'ITIL 4: Escalade automatique pour garantir une réponse rapide aux incidents critiques.',
        },
        {
          id: 'escalade_fonctionnelle',
          label: 'Souhaitez-vous permettre une escalade fonctionnelle (par expertise) ?',
          type: 'select',
          options: ['Oui', 'Non', 'En complément de l\'escalade hiérarchique'],
          helpText: 'ITIL 4: Escalade fonctionnelle pour accéder rapidement à l\'expertise nécessaire.',
        },
        {
          id: 'communication_escalade',
          label: 'Comment souhaitez-vous communiquer lors d\'une escalade ?',
          type: 'multiselect',
          options: [
            'Notification automatique par système',
            'Appel téléphonique',
            'Courriel',
            'Message Teams / Slack',
            'Réunion d\'urgence',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO): Communication claire essentielle lors de l\'escalade.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Processus & Documentation',
      description: 'Processus d\'escalade et documentation',
      icon: 'FileCheck',
      questions: [
        {
          id: 'processus_escalade',
          label: 'Quel processus d\'escalade souhaitez-vous documenter ?',
          type: 'select',
          options: [
            'Processus ITIL 4 complet',
            'Processus simplifié adapté',
            'Processus personnalisé',
            'À déterminer',
          ],
          helpText: 'ITIL 4: Processus structuré pour assurer une escalade efficace et cohérente.',
        },
        {
          id: 'documentation_escalade',
          label: 'Quelle documentation doit être créée lors d\'une escalade ?',
          type: 'multiselect',
          options: [
            'Journal d\'escalade',
            'Rapport d\'escalade',
            'Notification aux parties prenantes',
            'Mise à jour du ticket',
            'Documentation des actions prises',
            'Autre',
          ],
          helpText: 'ITIL 4: Documentation essentielle pour traçabilité et amélioration continue.',
        },
        {
          id: 'formation_escalade',
          label: 'Souhaitez-vous rendre la formation en escalade obligatoire ?',
          type: 'select',
          options: ['Oui, pour tous', 'Oui, pour l\'équipe TI uniquement', 'Non, formation recommandée', 'À déterminer'],
          helpText: 'ITIL 4: Formation essentielle pour assurer une escalade efficace.',
        },
        {
          id: 'revision_politique',
          label: 'À quelle fréquence souhaitez-vous réviser cette politique ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Lors de changements majeurs', 'À déterminer'],
          helpText: 'ITIL 4: Révision régulière essentielle pour maintenir la pertinence de la politique.',
        },
      ],
    },
  ],
};





